import dayjs from 'dayjs';

import { SearchSuggestDestinationResult } from '@hooks/useSearchSuggestDestinations';
import parseDate from '@utils/parseDate';

import { BookingTypeValues } from '../BookingType/BookingTypeTypes.js';

import { initialState, SearchFormState } from './SearchFormStore.js';
import {
  SearchFormDestinationDollhouseHotel,
  SearchFormDestinationDollhouseLocation,
  SearchFormDestinationHotel,
  SearchFormDestinationLocation,
  SearchFormDestinationType,
} from './SearchFormTypes.js';

const queryStringDateFormat = 'YYYY-MM-DD';
const queryStringTimeFormat = 'HH-mm';
const nonTimeZonedDateFormat = 'YYYY-MM-DDTHH:mm';

export type SearchFormQueryParams = Omit<SearchFormState, 'destination'> & {
  destination?: Partial<SearchFormState['destination']>;
};
export type SearchFormRouteParams = Pick<SearchFormState, 'destination'>;

const formatQueryStringDate = (date: string, time: string) => {
  const concatDate = `${date}T${time}`;
  const concatFormat = `${queryStringDateFormat}T${queryStringTimeFormat}`;
  const parsedDate = parseDate(concatDate, concatFormat);

  if (!parsedDate.isValid()) {
    throw new Error(`Invalid date "${concatDate}"`);
  }

  return parsedDate.format(nonTimeZonedDateFormat);
};

export const searchParamsToBookingType = (bookingTypeParam: string): BookingTypeValues => {
  const bookingTypes = Object.values(BookingTypeValues);
  return bookingTypes[Number(bookingTypeParam) - 1];
};

export const bookingTypeToSearchParams = (bookingType: BookingTypeValues): string => {
  return (Object.values(BookingTypeValues).indexOf(bookingType) + 1).toString();
};

export const parseSearchParams = (searchParams: URLSearchParams): SearchFormQueryParams => {
  const attendees = searchParams.get('attendees');
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const checkIn = searchParams.get('checkIn');
  const checkOut = searchParams.get('checkOut');
  const bookingTypeParam = searchParams.get('bookingType');
  const citySlug = searchParams.get('citySlug');
  const countrySlug = searchParams.get('countrySlug');
  const hotelSlug = searchParams.get('hotelSlug');
  const brandSlug = searchParams.get('brandSlug');

  const startDateRange = startDate && checkIn ? formatQueryStringDate(startDate, checkIn) : '';
  const endDateRange = endDate && checkOut ? formatQueryStringDate(endDate, checkOut) : '';

  const parsedBookingType = bookingTypeParam ? searchParamsToBookingType(bookingTypeParam) : null;
  const bookingType = parsedBookingType ? parsedBookingType : initialState.bookingType;

  let destination: Partial<SearchFormState['destination']> = null;
  if (citySlug && countrySlug) {
    destination = {
      type: SearchFormDestinationType.Location,
      citySlug,
      countrySlug,
    };

    if (hotelSlug && brandSlug) {
      destination = {
        ...destination,
        type: SearchFormDestinationType.Hotel,
        hotelSlug,
        brandSlug,
      };
    }
  }

  return {
    capacityOrRoomCount: attendees ? parseInt(attendees) : initialState.capacityOrRoomCount,
    dateRange: !startDateRange && !endDateRange ? null : `${startDateRange}/${endDateRange}`,
    bookingType,
    ...(destination ? { destination } : {}),
  };
};

export const formatSearchParams = (
  stateParams: Partial<SearchFormQueryParams>,
  destination?: SearchFormState['destination']
) => {
  const params = new URLSearchParams();

  if (stateParams.capacityOrRoomCount) {
    params.set('attendees', stateParams.capacityOrRoomCount.toString());
  }

  if (stateParams.dateRange) {
    const [startDate, endDate] = stateParams.dateRange.split('/');

    params.set('startDate', dayjs(startDate).format(queryStringDateFormat));
    params.set('checkIn', dayjs(startDate).format(queryStringTimeFormat));
    params.set('endDate', dayjs(endDate).format(queryStringDateFormat));
    params.set('checkOut', dayjs(endDate).format(queryStringTimeFormat));
  }

  if (stateParams.bookingType) {
    params.set('bookingType', bookingTypeToSearchParams(stateParams.bookingType));
  }

  // add params for location destination
  if (
    destination?.type === SearchFormDestinationType.Location &&
    destination?.citySlug &&
    destination?.countrySlug
  ) {
    params.set('citySlug', destination.citySlug);
    params.set('countrySlug', destination.countrySlug);
  }

  return `?${params.toString()}`;
};

export const parseSlugPayloadToDestination = (routeParams: {
  countrySlug?: string;
  citySlug?: string;
  hotelSlug?: string;
  brandSlug?: string;
}): SearchFormRouteParams => {
  let destination: SearchFormState['destination'] = null;

  if (routeParams.countrySlug && routeParams.citySlug) {
    destination = {
      type: SearchFormDestinationType.Location,
      countrySlug: routeParams.countrySlug,
      citySlug: routeParams.citySlug,
    };
    if (routeParams.hotelSlug && routeParams.brandSlug) {
      destination = {
        ...destination,
        type: SearchFormDestinationType.Hotel,
        hotelSlug: routeParams.hotelSlug,
        brandSlug: routeParams.brandSlug,
      };
    }
  }
  return { destination };
};

export const parseSuggestToDestination = (
  suggest: SearchSuggestDestinationResult
): SearchFormDestinationHotel | SearchFormDestinationLocation => {
  if (suggest?.type === 'Hotel') {
    return {
      type: SearchFormDestinationType.Hotel,
      brandSlug: suggest?.brand?.slug,
      citySlug: suggest?.city?.slug,
      countrySlug: suggest?.city?.country?.slug,
      hotelSlug: suggest?.slug,
      hotelId: suggest?.id.toString(),
    } as SearchFormDestinationHotel;
  } else {
    return {
      type: SearchFormDestinationType.Location,
      countrySlug: suggest?.country?.slug,
      citySlug: suggest?.slug,
      cityId: suggest?.id.toString(),
    };
  }
};

export const parseDollhouseSuggestToDestination = (
  suggest: SearchSuggestDestinationResult
): SearchFormDestinationDollhouseHotel | SearchFormDestinationDollhouseLocation => {
  if (suggest?.type === 'Hotel') {
    return {
      type: SearchFormDestinationType.DollhouseHotel,
      hotelSlug: suggest?.slug,
    };
  } else {
    return {
      type: SearchFormDestinationType.DollhouseLocation,
      countrySlug: suggest?.country?.slug,
      citySlug: suggest?.slug,
      cityId: suggest?.id.toString(),
    };
  }
};
